import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Car, Upload, Trash2, Edit2, Plus, Loader2, Image, Link2, FileSpreadsheet, ExternalLink, Search, ChevronRight, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_LIST = [
  { make: 'Buick', model: 'Enclave Avenir' },
  { make: 'Buick', model: 'Enclave Preferred' },
  { make: 'Buick', model: 'Enclave ST' },
  { make: 'Buick', model: 'Encore GX Avenir' },
  { make: 'Buick', model: 'Encore GX Preferred' },
  { make: 'Buick', model: 'Encore GX ST' },
  { make: 'Buick', model: 'Envision Avenir' },
  { make: 'Buick', model: 'Envision Preferred' },
  { make: 'Buick', model: 'Envision ST' },
  { make: 'Buick', model: 'Envista Avenir' },
  { make: 'Buick', model: 'Envista Preferred' },
  { make: 'Buick', model: 'Envista ST' },
  { make: 'Cadillac', model: 'CT4' },
  { make: 'Cadillac', model: 'CT4 V-Series' },
  { make: 'Cadillac', model: 'CT5' },
  { make: 'Cadillac', model: 'CT5 V-Series' },
  { make: 'Cadillac', model: 'Escalade' },
  { make: 'Cadillac', model: 'Escalade ESV' },
  { make: 'Cadillac', model: 'Escalade IQ' },
  { make: 'Cadillac', model: 'Escalade IQL' },
  { make: 'Cadillac', model: 'Escalade-V' },
  { make: 'Cadillac', model: 'Escalade-V ESV' },
  { make: 'Cadillac', model: 'Lyriq' },
  { make: 'Cadillac', model: 'Lyriq-V' },
  { make: 'Cadillac', model: 'Optiq' },
  { make: 'Cadillac', model: 'Optiq-V' },
  { make: 'Cadillac', model: 'Vistiq' },
  { make: 'Cadillac', model: 'XT5' },
  { make: 'Chevrolet', model: 'Blazer' },
  { make: 'Chevrolet', model: 'Blazer EV' },
  { make: 'Chevrolet', model: 'Bolt' },
  { make: 'Chevrolet', model: 'Colorado' },
  { make: 'Chevrolet', model: 'Corvette E-Ray' },
  { make: 'Chevrolet', model: 'Corvette Stingray' },
  { make: 'Chevrolet', model: 'Corvette Z06' },
  { make: 'Chevrolet', model: 'Corvette ZR1' },
  { make: 'Chevrolet', model: 'Corvette ZR1X' },
  { make: 'Chevrolet', model: 'Equinox' },
  { make: 'Chevrolet', model: 'Equinox EV' },
  { make: 'Chevrolet', model: 'Express Cargo' },
  { make: 'Chevrolet', model: 'Express Passenger' },
  { make: 'Chevrolet', model: 'Silverado 1500' },
  { make: 'Chevrolet', model: 'Silverado 2500 HD' },
  { make: 'Chevrolet', model: 'Silverado 3500 HD' },
  { make: 'Chevrolet', model: 'Silverado EV' },
  { make: 'Chevrolet', model: 'Suburban' },
  { make: 'Chevrolet', model: 'Tahoe' },
  { make: 'Chevrolet', model: 'Trailblazer' },
  { make: 'Chevrolet', model: 'Traverse' },
  { make: 'Chevrolet', model: 'Trax' },
  { make: 'GMC', model: 'Acadia' },
  { make: 'GMC', model: 'Canyon' },
  { make: 'GMC', model: 'Hummer EV Pickup' },
  { make: 'GMC', model: 'Hummer EV SUV' },
  { make: 'GMC', model: 'Savana Cargo' },
  { make: 'GMC', model: 'Savana Passenger' },
  { make: 'GMC', model: 'Sierra 1500' },
  { make: 'GMC', model: 'Sierra 2500 HD' },
  { make: 'GMC', model: 'Sierra 3500 HD' },
  { make: 'GMC', model: 'Sierra EV' },
  { make: 'GMC', model: 'Terrain' },
  { make: 'GMC', model: 'Yukon' },
  { make: 'GMC', model: 'Yukon XL' },
];

export default function RepositoryPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Vehicle Repository state
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({ vehicle_make: '', vehicle_model: '', build_price_url: '' });
  const [vehicleImageFile, setVehicleImageFile] = useState(null);
  const [vehicleImagePreview, setVehicleImagePreview] = useState(null);
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const imageInputRef = useRef(null);
  const csvVehicleRef = useRef(null);

  // Inventory Links state
  const [inventoryLinks, setInventoryLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [showAddLink, setShowAddLink] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm, setLinkForm] = useState({ dealership_id: '', vehicle_make: '', vehicle_model: '', inventory_url: '', alias_email: '' });
  const [savingLink, setSavingLink] = useState(false);
  const [dealerships, setDealerships] = useState([]);
  const [filterDealership, setFilterDealership] = useState('all');
  const [filterAlias, setFilterAlias] = useState('all');
  const csvInventoryRef = useRef(null);
  const [importDealership, setImportDealership] = useState('');

  // Search
  const [searchVehicle, setSearchVehicle] = useState('');
  const [searchLink, setSearchLink] = useState('');

  // Add Make/Model dialog
  const [showAddMakeModel, setShowAddMakeModel] = useState(false);
  const [newMake, setNewMake] = useState('');
  const [newModel, setNewModel] = useState('');
  const [addingMakeModel, setAddingMakeModel] = useState(false);
  const [openMake, setOpenMake] = useState(null);
  const [openLinkMake, setOpenLinkMake] = useState(null);

  useEffect(() => {
    fetchVehicles();
    fetchInventoryLinks();
    fetchDealerships();
  }, []);

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API}/vehicle-repository`, { headers, withCredentials: true });
      setVehicles(res.data);
    } catch { console.log('Failed to fetch vehicles'); }
    setLoadingVehicles(false);
  };

  const fetchInventoryLinks = async () => {
    try {
      const res = await axios.get(`${API}/dealer-inventory-links`, { headers, withCredentials: true });
      setInventoryLinks(res.data);
    } catch { console.log('Failed to fetch inventory links'); }
    setLoadingLinks(false);
  };

  const fetchDealerships = async () => {
    try {
      const res = await axios.get(`${API}/dealerships`, { headers, withCredentials: true });
      setDealerships(res.data);
    } catch (err) { console.error('Failed to fetch dealerships:', err); }
  };

  // ---- Vehicle Repository CRUD ----
  const saveVehicle = async () => {
    if (!vehicleForm.vehicle_make.trim() || !vehicleForm.vehicle_model.trim()) return;
    setSavingVehicle(true);
    try {
      let entryId = editingVehicle;
      if (editingVehicle) {
        // If user deleted the image (both preview and file are null), clear image_url in DB
        const payload = { ...vehicleForm };
        if (!vehicleImagePreview && !vehicleImageFile) {
          payload.image_url = '';
        }
        await axios.put(`${API}/vehicle-repository/${editingVehicle}`, payload, { headers, withCredentials: true });
      } else {
        const res = await axios.post(`${API}/vehicle-repository`, vehicleForm, { headers, withCredentials: true });
        entryId = res.data.entry_id;
      }
      // Upload image if selected
      if (vehicleImageFile && entryId) {
        const formData = new FormData();
        formData.append('file', vehicleImageFile);
        await axios.post(`${API}/vehicle-repository/${entryId}/upload-image`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true
        });
      }
      toast.success(editingVehicle ? 'Vehicle updated' : 'Vehicle added');
      setShowAddVehicle(false);
      setEditingVehicle(null);
      setVehicleForm({ vehicle_make: '', vehicle_model: '', build_price_url: '' });
      setVehicleImageFile(null);
      setVehicleImagePreview(null);
      fetchVehicles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSavingVehicle(false);
  };

  const deleteVehicle = async (entryId) => {
    if (!window.confirm('Delete this vehicle entry?')) return;
    try {
      await axios.delete(`${API}/vehicle-repository/${entryId}`, { headers, withCredentials: true });
      toast.success('Vehicle removed');
      fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const editVehicle = (v) => {
    setEditingVehicle(v.entry_id);
    setVehicleForm({ vehicle_make: v.vehicle_make, vehicle_model: v.vehicle_model, build_price_url: v.build_price_url || '' });
    setVehicleImageFile(null);
    setVehicleImagePreview(v.image_url || null);
    setShowAddVehicle(true);
  };

  const uploadImage = async (entryId, file) => {
    setUploadingImage(entryId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/vehicle-repository/${entryId}/upload-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      toast.success('Image uploaded');
      fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploadingImage(null);
  };

  const importVehicleCsv = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/vehicle-repository/import-csv`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      toast.success(`Imported ${res.data.imported} vehicles (${res.data.skipped} skipped)`);
      fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || 'CSV import failed'); }
  };

  // ---- Inventory Links CRUD ----
  const saveLink = async () => {
    if (!linkForm.dealership_id || !linkForm.vehicle_make.trim() || !linkForm.vehicle_model.trim() || !linkForm.inventory_url.trim()) return;
    setSavingLink(true);
    try {
      const payload = { ...linkForm, alias_email: linkForm.alias_email === '__none__' ? '' : (linkForm.alias_email || '') };
      if (editingLink) {
        await axios.put(`${API}/dealer-inventory-links/${editingLink}`, payload, { headers, withCredentials: true });
        toast.success('Link updated');
      } else {
        await axios.post(`${API}/dealer-inventory-links`, payload, { headers, withCredentials: true });
        toast.success('Link added');
      }
      setShowAddLink(false);
      setEditingLink(null);
      setLinkForm({ dealership_id: '', vehicle_make: '', vehicle_model: '', inventory_url: '', alias_email: '' });
      fetchInventoryLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSavingLink(false);
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm('Delete this inventory link?')) return;
    try {
      await axios.delete(`${API}/dealer-inventory-links/${linkId}`, { headers, withCredentials: true });
      toast.success('Link removed');
      fetchInventoryLinks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const editLink = (l) => {
    setEditingLink(l.link_id);
    setLinkForm({ dealership_id: l.dealership_id, vehicle_make: l.vehicle_make, vehicle_model: l.vehicle_model, inventory_url: l.inventory_url });
    setShowAddLink(true);
  };

  const importInventoryCsv = async (file) => {
    if (!importDealership) {
      toast.error('Select a dealership first');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dealership_id', importDealership);
    try {
      const res = await axios.post(`${API}/dealer-inventory-links/import-csv`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      toast.success(`Imported ${res.data.imported} links (${res.data.skipped} skipped)`);
      fetchInventoryLinks();
    } catch (err) { toast.error(err.response?.data?.detail || 'CSV import failed'); }
  };

  // Filtered data (memoized)
  const filteredVehicles = useMemo(() => vehicles.filter(v =>
    !searchVehicle || `${v.vehicle_make} ${v.vehicle_model}`.toLowerCase().includes(searchVehicle.toLowerCase())
  ), [vehicles, searchVehicle]);

  const filteredLinks = useMemo(() => inventoryLinks.filter(l => {
    const matchSearch = !searchLink || `${l.vehicle_make} ${l.vehicle_model}`.toLowerCase().includes(searchLink.toLowerCase());
    const matchDealer = filterDealership === 'all' || l.dealership_id === filterDealership;
    const matchAlias = filterAlias === 'all' || (filterAlias === '__unassigned__' ? !(l.alias_email) : (l.alias_email || '') === filterAlias);
    return matchSearch && matchDealer && matchAlias;
  }), [inventoryLinks, searchLink, filterDealership, filterAlias]);

  const getDealerName = (id) => dealerships.find(d => d.dealership_id === id)?.name || id;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="repository-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
          Repository
        </h1>
        <p className="text-slate-400 text-sm mt-2">Vehicle images, Build &amp; Price links, and dealership inventory URLs</p>
      </div>

      <Tabs defaultValue="vehicles" className="space-y-6">
        <TabsList className="bg-[#0A0A0A] border border-[#262626]">
          <TabsTrigger value="vehicles" className="data-[state=active]:bg-red-600/10 data-[state=active]:text-red-400 text-xs font-bold tracking-widest uppercase" data-testid="tab-vehicles">
            <Car className="w-3.5 h-3.5 mr-1.5" /> Vehicle Images &amp; Specs
          </TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-red-600/10 data-[state=active]:text-red-400 text-xs font-bold tracking-widest uppercase" data-testid="tab-inventory">
            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Inventory Links
          </TabsTrigger>
          <TabsTrigger value="makes-models" className="data-[state=active]:bg-red-600/10 data-[state=active]:text-red-400 text-xs font-bold tracking-widest uppercase" data-testid="tab-makes-models">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Makes &amp; Models
          </TabsTrigger>
        </TabsList>

        {/* ==================== VEHICLE IMAGES & BUILD & PRICE ==================== */}
        <TabsContent value="vehicles" className="space-y-4">
          <Card className="bg-[#0A0A0A] border-[#262626] p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={searchVehicle}
                    onChange={e => setSearchVehicle(e.target.value)}
                    placeholder="Search vehicles..."
                    className="pl-9 bg-[#111] border-[#333] h-9 text-sm"
                    data-testid="search-vehicles-input"
                  />
                </div>
                <Badge className="bg-slate-500/10 text-slate-400 border border-slate-500/20 text-[9px] font-bold">
                  {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => { setEditingVehicle(null); setVehicleForm({ vehicle_make: '', vehicle_model: '', build_price_url: '' }); setVehicleImageFile(null); setVehicleImagePreview(null); setShowAddVehicle(true); }}
                  className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold tracking-wider uppercase h-9"
                  data-testid="add-vehicle-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Vehicle
                </Button>
              </div>
            </div>

            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-3 mb-4">
              <p className="text-xs text-slate-400 leading-relaxed">
                Upload vehicle images and Build &amp; Price links. Use <code className="text-slate-200 bg-[#1a1a1a] px-1 rounded">{'{{vehicle_image}}'}</code> in 
                templates to show a clickable vehicle image linking to the Build &amp; Price page. <strong className="text-slate-300">Global</strong> — shared across all dealerships.
              </p>
              <p className="text-[10px] text-slate-500 mt-1">
                CSV format: <code className="text-slate-400">vehicle_make, vehicle_model, build_price_url</code>
              </p>
            </div>

            {loadingVehicles ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
            ) : filteredVehicles.length === 0 && !openMake ? (
              <div className="text-center py-12">
                <Car className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                <p className="text-sm text-slate-500">{searchVehicle ? 'No matching vehicles' : 'No vehicles added yet'}</p>
              </div>
            ) : openMake ? (
              /* ---- EXPANDED MAKE VIEW: Show all models for selected make ---- */
              (() => {
                const makeVehicles = filteredVehicles
                  .filter(v => v.vehicle_make === openMake)
                  .sort((a, b) => a.vehicle_model.localeCompare(b.vehicle_model));
                const firstImage = makeVehicles.find(v => v.image_url);
                return (
                  <div data-testid={`vehicle-group-${openMake}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        onClick={() => setOpenMake(null)}
                        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                        data-testid="back-to-makes-btn"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-xs font-bold tracking-widest uppercase">All Makes</span>
                      </button>
                      <div className="h-4 w-px bg-[#333]" />
                      <h3 className="font-heading text-lg font-bold text-white uppercase tracking-wider">{openMake}</h3>
                      <Badge className="bg-red-500/10 text-red-400 border border-red-500/20 text-[8px] font-bold tracking-wider">
                        {makeVehicles.length} model{makeVehicles.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {makeVehicles.map(v => (
                        <div key={v.entry_id} className="bg-[#111] border border-[#1A1A1A] rounded-lg overflow-hidden hover:border-[#333] transition-colors" data-testid={`vehicle-${v.entry_id}`}>
                          <div className="relative h-36 bg-[#0A0A0A] flex items-center justify-center group">
                            {v.image_url ? (
                              <img src={v.image_url} alt={`${v.vehicle_make} ${v.vehicle_model}`} className="w-full h-full object-contain p-2" />
                            ) : (
                              <div className="text-center">
                                <Image className="w-8 h-8 mx-auto text-slate-600 mb-1" />
                                <p className="text-[10px] text-slate-600">No image</p>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Button
                                size="sm"
                                onClick={() => { imageInputRef.current?.setAttribute('data-entry', v.entry_id); imageInputRef.current?.click(); }}
                                disabled={uploadingImage === v.entry_id}
                                className="bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold tracking-wider uppercase h-8 backdrop-blur-sm"
                                data-testid={`upload-image-${v.entry_id}`}
                              >
                                {uploadingImage === v.entry_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}
                              </Button>
                            </div>
                          </div>
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-semibold text-white">{v.vehicle_model}</p>
                              <div className="flex gap-0.5">
                                <button onClick={() => editVehicle(v)} className="p-1.5 text-slate-500 hover:text-white rounded transition-colors" data-testid={`edit-vehicle-${v.entry_id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                                <button onClick={() => deleteVehicle(v.entry_id)} className="p-1.5 text-slate-500 hover:text-red-400 rounded transition-colors" data-testid={`delete-vehicle-${v.entry_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                            {v.build_price_url ? (
                              <a href={v.build_price_url} target="_blank" rel="noreferrer" className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 truncate">
                                <ExternalLink className="w-3 h-3 flex-shrink-0" /> Specs
                              </a>
                            ) : (
                              <p className="text-[10px] text-slate-600">No Specs URL</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
            ) : (
              /* ---- MAKE CARDS: Clickable cards for each make ---- */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="makes-grid">
                {Object.entries(
                  filteredVehicles.reduce((groups, v) => {
                    const make = v.vehicle_make || 'Other';
                    if (!groups[make]) groups[make] = [];
                    groups[make].push(v);
                    return groups;
                  }, {})
                ).sort(([a], [b]) => a.localeCompare(b)).map(([make, items]) => {
                  const withImages = items.filter(v => v.image_url);
                  const previewImages = withImages.slice(0, 3);
                  return (
                    <button
                      key={make}
                      onClick={() => { setOpenMake(make); setSearchVehicle(''); }}
                      className="bg-[#111] border border-[#1A1A1A] rounded-lg p-5 hover:border-red-500/40 hover:bg-[#0F0F0F] transition-all text-left group"
                      data-testid={`make-card-${make}`}
                    >
                      {/* Preview thumbnails */}
                      <div className="flex items-center gap-2 mb-4 h-20">
                        {previewImages.length > 0 ? (
                          previewImages.map((v, i) => (
                            <div key={v.entry_id} className="flex-1 h-full bg-[#0A0A0A] rounded-md overflow-hidden border border-[#1A1A1A]">
                              <img src={v.image_url} alt={v.vehicle_model} className="w-full h-full object-contain p-1" />
                            </div>
                          ))
                        ) : (
                          <div className="flex-1 h-full bg-[#0A0A0A] rounded-md flex items-center justify-center border border-[#1A1A1A]">
                            <Car className="w-8 h-8 text-slate-700" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-heading text-lg font-bold text-white uppercase tracking-wider group-hover:text-red-400 transition-colors">
                            {make}
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {items.length} model{items.length !== 1 ? 's' : ''}
                            {withImages.length > 0 && <span className="text-slate-600"> &middot; {withImages.length} with images</span>}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-400 transition-colors" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ==================== INVENTORY LINKS ==================== */}
        <TabsContent value="inventory" className="space-y-4">
          <Card className="bg-[#0A0A0A] border-[#262626] p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={searchLink}
                    onChange={e => setSearchLink(e.target.value)}
                    placeholder="Search models..."
                    className="pl-9 bg-[#111] border-[#333] h-9 text-sm"
                    data-testid="search-links-input"
                  />
                </div>
                <Select value={filterDealership} onValueChange={v => { setFilterDealership(v); setFilterAlias('all'); }}>
                  <SelectTrigger className="bg-[#111] border-[#333] h-9 text-sm w-[200px]" data-testid="filter-dealership-select">
                    <SelectValue placeholder="All dealerships" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626]">
                    <SelectItem value="all" className="text-white hover:bg-[#262626]">All Dealerships</SelectItem>
                    {dealerships.map(d => (
                      <SelectItem key={d.dealership_id} value={d.dealership_id} className="text-white hover:bg-[#262626]">{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Alias filter — only for VinSolutions dealerships with aliases */}
                {(() => {
                  const selDlr = dealerships.find(d => d.dealership_id === filterDealership);
                  const aliases = selDlr?.email_aliases || [];
                  if (aliases.length === 0) return null;
                  return (
                    <Select value={filterAlias} onValueChange={setFilterAlias}>
                      <SelectTrigger className="bg-[#111] border-[#333] h-9 text-sm w-[200px]" data-testid="filter-alias-select">
                        <SelectValue placeholder="All aliases" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#171717] border-[#262626]">
                        <SelectItem value="all" className="text-white hover:bg-[#262626]">All Aliases</SelectItem>
                        <SelectItem value="__unassigned__" className="text-slate-400 hover:bg-[#262626]">No alias assigned</SelectItem>
                        {aliases.map((a, idx) => {
                          const website = a.website ? a.website.replace('https://','').replace('http://','').replace('www.','') : '';
                          return (
                            <SelectItem key={a.alias || idx} value={a.alias} className="text-white hover:bg-[#262626]">
                              {website || a.alias}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  );
                })()}
                <Badge className="bg-slate-500/10 text-slate-400 border border-slate-500/20 text-[9px] font-bold">
                  {filteredLinks.length} link{filteredLinks.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <Button
                  size="sm"
                  onClick={() => { setEditingLink(null); setLinkForm({ dealership_id: '', vehicle_make: '', vehicle_model: '', inventory_url: '', alias_email: '' }); setShowAddLink(true); }}
                  className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold tracking-wider uppercase h-9"
                  data-testid="add-inventory-link-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Link
                </Button>
              </div>
            </div>

            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-3 mb-4">
              <p className="text-xs text-slate-400 leading-relaxed">
                Dealership-specific inventory links. Use <code className="text-slate-200 bg-[#1a1a1a] px-1 rounded">{'{{inventory_link}}'}</code> in 
                templates — it renders as a clickable "online inventory" link to the matching dealership + model URL.
              </p>
              <p className="text-[10px] text-slate-500 mt-1">
                CSV format: <code className="text-slate-400">vehicle_make, vehicle_model, inventory_url</code>
              </p>
            </div>

            {loadingLinks ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
            ) : filteredLinks.length === 0 && !openLinkMake ? (
              <div className="text-center py-12">
                <Link2 className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                <p className="text-sm text-slate-500">{searchLink || filterDealership !== 'all' ? 'No matching links' : 'No inventory links added yet'}</p>
              </div>
            ) : openLinkMake ? (
              /* ---- EXPANDED MAKE VIEW: Show all models for selected make ---- */
              (() => {
                const makeLinks = filteredLinks
                  .filter(l => l.vehicle_make === openLinkMake)
                  .sort((a, b) => a.vehicle_model.localeCompare(b.vehicle_model));
                return (
                  <div data-testid={`link-group-${openLinkMake}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        onClick={() => setOpenLinkMake(null)}
                        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                        data-testid="back-to-link-makes-btn"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-xs font-bold tracking-widest uppercase">All Makes</span>
                      </button>
                      <div className="h-4 w-px bg-[#333]" />
                      <h3 className="font-heading text-lg font-bold text-white uppercase tracking-wider">{openLinkMake}</h3>
                      <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[8px] font-bold tracking-wider">
                        {makeLinks.length} link{makeLinks.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {makeLinks.map(l => (
                        <div key={l.link_id} className="bg-[#111] border border-[#1A1A1A] rounded-lg p-3 flex items-center justify-between hover:border-[#333] transition-colors" data-testid={`inv-link-${l.link_id}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                              <Link2 className="w-4 h-4 text-blue-400" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-white">{l.vehicle_model}</p>
                                <Badge className="bg-slate-500/10 text-slate-400 border border-slate-500/20 text-[8px] font-bold tracking-wider uppercase">
                                  {getDealerName(l.dealership_id)}
                                </Badge>
                                {l.alias_email && (
                                  <Badge className="bg-purple-500/10 text-purple-400 border border-purple-500/20 text-[8px] font-bold tracking-wider">
                                    {l.alias_email}
                                  </Badge>
                                )}
                              </div>
                              <a href={l.inventory_url} target="_blank" rel="noreferrer" className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 truncate max-w-md">
                                <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" /> {l.inventory_url}
                              </a>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button onClick={() => editLink(l)} className="p-1.5 text-slate-500 hover:text-white rounded transition-colors" data-testid={`edit-link-${l.link_id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deleteLink(l.link_id)} className="p-1.5 text-slate-500 hover:text-red-400 rounded transition-colors" data-testid={`delete-link-${l.link_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
            ) : (
              /* ---- MAKE CARDS: Clickable cards for each make ---- */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="link-makes-grid">
                {Object.entries(
                  filteredLinks.reduce((groups, l) => {
                    const make = l.vehicle_make || 'Other';
                    if (!groups[make]) groups[make] = [];
                    groups[make].push(l);
                    return groups;
                  }, {})
                ).sort(([a], [b]) => a.localeCompare(b)).map(([make, items]) => {
                  const dealerCount = new Set(items.map(l => l.dealership_id)).size;
                  return (
                    <button
                      key={make}
                      onClick={() => { setOpenLinkMake(make); setSearchLink(''); }}
                      className="bg-[#111] border border-[#1A1A1A] rounded-lg p-5 hover:border-blue-500/40 hover:bg-[#0F0F0F] transition-all text-left group"
                      data-testid={`link-make-card-${make}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Link2 className="w-5 h-5 text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap gap-1">
                            {items.slice(0, 3).map(l => (
                              <Badge key={l.link_id} className="bg-[#0A0A0A] text-slate-400 border border-[#1A1A1A] text-[8px] font-medium truncate max-w-[120px]">
                                {l.vehicle_model}
                              </Badge>
                            ))}
                            {items.length > 3 && (
                              <Badge className="bg-[#0A0A0A] text-slate-500 border border-[#1A1A1A] text-[8px]">
                                +{items.length - 3}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-heading text-lg font-bold text-white uppercase tracking-wider group-hover:text-blue-400 transition-colors">
                            {make}
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {items.length} link{items.length !== 1 ? 's' : ''}
                            <span className="text-slate-600"> &middot; {dealerCount} dealership{dealerCount !== 1 ? 's' : ''}</span>
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400 transition-colors" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ==================== MAKES & MODELS ==================== */}
        <TabsContent value="makes-models" className="space-y-4">
          <Card className="bg-[#0A0A0A] border-[#262626] p-4">
            {(() => {
              const makeGroups = {};
              vehicles.forEach(v => {
                const mk = v.vehicle_make || 'Unknown';
                if (!makeGroups[mk]) makeGroups[mk] = [];
                makeGroups[mk].push(v);
              });
              const sortedMakes = Object.keys(makeGroups).sort();
              const totalModels = vehicles.length;

              return (
                <>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-bold tracking-widest text-slate-400 uppercase">All Makes &amp; Models</h3>
                      <Badge className="bg-slate-500/10 text-slate-400 border border-slate-500/20 text-[9px] font-bold">
                        {sortedMakes.length} makes &middot; {totalModels} models
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setShowAddMakeModel(true)}
                      className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold tracking-wider uppercase h-9"
                      data-testid="add-make-model-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Make / Model
                    </Button>
                  </div>

                  <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-3 mb-4">
                    <p className="text-xs text-slate-400 leading-relaxed">
                      All vehicle makes and models in the repository. These are used for image matching, template variables, and inventory link lookups.
                    </p>
                  </div>

                  {sortedMakes.length === 0 ? (
                    <div className="text-center py-12">
                      <Car className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                      <p className="text-sm text-slate-500">No makes or models in the repository yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedMakes.map(make => {
                        const models = makeGroups[make].sort((a, b) => (a.vehicle_model || '').localeCompare(b.vehicle_model || ''));
                        return (
                          <div key={make} className="bg-[#111] border border-[#1A1A1A] rounded-lg overflow-hidden" data-testid={`make-group-${make}`}>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A1A1A]">
                              <div className="flex items-center gap-3">
                                <h4 className="font-heading text-sm font-bold text-white uppercase tracking-wider">{make}</h4>
                                <Badge className="bg-slate-500/10 text-slate-400 border border-slate-500/20 text-[8px] font-bold">
                                  {models.length}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 p-3">
                              {models.map(v => (
                                <div
                                  key={v.entry_id}
                                  className="group flex items-center gap-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md px-2.5 py-1.5 hover:border-red-500/30 transition-colors"
                                  data-testid={`model-chip-${v.entry_id}`}
                                >
                                  <span className="text-xs text-slate-300">{v.vehicle_model || '(base)'}</span>
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm(`Remove ${make} ${v.vehicle_model}?\n\nThis will also remove any inventory links for this vehicle.`)) return;
                                      try {
                                        await axios.delete(`${API}/vehicle-repository/${v.entry_id}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
                                        // Also delete inventory links for this make/model
                                        const matchingLinks = inventoryLinks.filter(l => l.vehicle_make === make && l.vehicle_model === v.vehicle_model);
                                        for (const link of matchingLinks) {
                                          try {
                                            await axios.delete(`${API}/dealer-inventory-links/${link.link_id}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
                                          } catch (err) { console.error('Failed to delete inventory link:', err); }
                                        }
                                        if (matchingLinks.length > 0) {
                                          setInventoryLinks(prev => prev.filter(l => !(l.vehicle_make === make && l.vehicle_model === v.vehicle_model)));
                                        }
                                        toast.success(`Removed ${make} ${v.vehicle_model}${matchingLinks.length ? ` and ${matchingLinks.length} inventory link${matchingLinks.length > 1 ? 's' : ''}` : ''}`);
                                        setVehicles(prev => prev.filter(x => x.entry_id !== v.entry_id));
                                      } catch (err) {
                                        toast.error(err?.response?.data?.detail || 'Failed to remove');
                                      }
                                    }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-red-400 ml-0.5"
                                    data-testid={`remove-model-${v.entry_id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Hidden file input for vehicle images */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => {
          const file = e.target.files[0];
          const entryId = e.target.getAttribute('data-entry');
          if (file && entryId) uploadImage(entryId, file);
          e.target.value = '';
        }}
      />

      {/* Add/Edit Vehicle Dialog */}
      <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
              {editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Vehicle Selection Dropdown */}
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Vehicle</Label>
              {editingVehicle ? (
                <div className="mt-1.5 bg-[#111] border border-[#333] rounded-md h-11 flex items-center px-3 text-sm text-white">
                  {vehicleForm.vehicle_make} {vehicleForm.vehicle_model}
                </div>
              ) : (
                <Select
                  value={vehicleForm.vehicle_model ? `${vehicleForm.vehicle_make}|${vehicleForm.vehicle_model}` : ""}
                  onValueChange={v => {
                    const [make, ...modelParts] = v.split('|');
                    setVehicleForm(p => ({ ...p, vehicle_make: make, vehicle_model: modelParts.join('|') }));
                  }}
                >
                  <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="vehicle-select">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626] max-h-72">
                    {VEHICLE_LIST.filter(v => !vehicles.some(
                      existing => existing.vehicle_make === v.make && existing.vehicle_model === v.model
                    )).map(v => (
                      <SelectItem key={`${v.make}|${v.model}`} value={`${v.make}|${v.model}`} className="text-white hover:bg-[#262626]">
                        {v.make} {v.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Vehicle Image Upload */}
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Vehicle Image</Label>
              <div className="mt-1.5">
                {(vehicleImagePreview || vehicleImageFile) ? (
                  <div className="relative bg-[#111] border border-[#333] rounded-lg overflow-hidden">
                    <img
                      src={vehicleImageFile ? URL.createObjectURL(vehicleImageFile) : vehicleImagePreview}
                      alt="Vehicle preview"
                      className="w-full h-40 object-contain p-2"
                    />
                    <button
                      type="button"
                      onClick={() => { setVehicleImageFile(null); setVehicleImagePreview(null); }}
                      className="absolute top-2 right-2 bg-black/70 hover:bg-black p-1.5 rounded-md text-slate-300 hover:text-white transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 bg-[#111] border-2 border-dashed border-[#333] rounded-lg cursor-pointer hover:border-red-500/50 transition-colors" data-testid="vehicle-image-upload-area">
                    <Upload className="w-6 h-6 text-slate-500 mb-2" />
                    <span className="text-xs text-slate-500">Click to upload image</span>
                    <span className="text-[10px] text-slate-600 mt-0.5">JPG, PNG, WebP, GIF</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file) { setVehicleImageFile(file); setVehicleImagePreview(null); }
                      }}
                      data-testid="vehicle-image-file-input"
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Build &amp; Price URL</Label>
              <Input
                value={vehicleForm.build_price_url}
                onChange={e => setVehicleForm(p => ({ ...p, build_price_url: e.target.value }))}
                placeholder="https://www.chevrolet.com/build-and-price/colorado"
                className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm font-mono text-xs"
                data-testid="build-price-url-input"
              />
            </div>
            <Button onClick={saveVehicle} disabled={savingVehicle || !vehicleForm.vehicle_make || !vehicleForm.vehicle_model}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11" data-testid="save-vehicle-btn">
              {savingVehicle ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingVehicle ? 'Update Vehicle' : 'Add Vehicle')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Inventory Link Dialog */}
      <Dialog open={showAddLink} onOpenChange={setShowAddLink}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
              {editingLink ? 'Edit Inventory Link' : 'Add Inventory Link'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Dealership</Label>
              <Select value={linkForm.dealership_id} onValueChange={v => setLinkForm(p => ({ ...p, dealership_id: v, vehicle_make: '', vehicle_model: '', alias_email: '' }))}>
                <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="link-dealership-select">
                  <SelectValue placeholder="Select dealership" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  {dealerships.map(d => (
                    <SelectItem key={d.dealership_id} value={d.dealership_id} className="text-white hover:bg-[#262626]">{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Alias selector — shows when selected dealership has email_aliases */}
            {(() => {
              const selectedDlr = dealerships.find(d => d.dealership_id === linkForm.dealership_id);
              const aliases = selectedDlr?.email_aliases || [];
              if (aliases.length === 0) return null;
              return (
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Alias / Sub-Dealership</Label>
                  <Select value={linkForm.alias_email || ""} onValueChange={v => setLinkForm(p => ({ ...p, alias_email: v, vehicle_make: '', vehicle_model: '' }))}>
                    <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm overflow-hidden" data-testid="link-alias-select">
                      <SelectValue placeholder="Select alias" className="truncate" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#171717] border-[#262626] max-w-[calc(100vw-3rem)]">
                      <SelectItem value="__none__" className="text-slate-400 hover:bg-[#262626]">All / No specific alias</SelectItem>
                      {aliases.map((a, idx) => {
                        const website = a.website ? a.website.replace('https://', '').replace('http://', '').replace('www.', '') : '';
                        const label = website || a.alias || `Alias ${idx + 1}`;
                        return (
                          <SelectItem key={a.alias || idx} value={a.alias || `alias_${idx}`} className="text-white hover:bg-[#262626]">
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Vehicle</Label>
              {editingLink ? (
                <div className="mt-1.5 bg-[#111] border border-[#333] rounded-md h-11 flex items-center px-3 text-sm text-white">
                  {linkForm.vehicle_make} {linkForm.vehicle_model}
                </div>
              ) : (
                <Select
                  value={linkForm.vehicle_model ? `${linkForm.vehicle_make}|${linkForm.vehicle_model}` : ""}
                  onValueChange={v => {
                    const [make, ...modelParts] = v.split('|');
                    setLinkForm(p => ({ ...p, vehicle_make: make, vehicle_model: modelParts.join('|') }));
                  }}
                >
                  <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="link-vehicle-select">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626] max-h-72">
                    {(() => {
                      const selectedDlr = dealerships.find(d => d.dealership_id === linkForm.dealership_id);
                      // Use alias-level brands if an alias is selected, otherwise dealership-level brands
                      let activeBrands = selectedDlr?.brands || [];
                      if (linkForm.alias_email && linkForm.alias_email !== '__none__') {
                        const selectedAlias = (selectedDlr?.email_aliases || []).find(a => a.alias === linkForm.alias_email);
                        if (selectedAlias?.brands?.length > 0) {
                          activeBrands = selectedAlias.brands;
                        }
                      }
                      const repoVehicles = vehicles.map(v => ({ make: v.vehicle_make, model: v.vehicle_model }));
                      return repoVehicles
                        .filter(v => activeBrands.length === 0 || activeBrands.some(b => b.toLowerCase() === v.make.toLowerCase()))
                        .filter(v => !inventoryLinks.some(
                          existing => existing.dealership_id === linkForm.dealership_id 
                            && existing.vehicle_make === v.make 
                            && existing.vehicle_model === v.model
                            && (existing.alias_email || '') === (linkForm.alias_email === '__none__' ? '' : (linkForm.alias_email || ''))
                        ))
                        .sort((a, b) => a.make.localeCompare(b.make) || a.model.localeCompare(b.model))
                        .map(v => (
                          <SelectItem key={`${v.make}|${v.model}`} value={`${v.make}|${v.model}`} className="text-white hover:bg-[#262626]">
                            {v.make} {v.model}
                          </SelectItem>
                        ));
                    })()}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Inventory URL</Label>
              <Input value={linkForm.inventory_url} onChange={e => setLinkForm(p => ({ ...p, inventory_url: e.target.value }))}
                placeholder="https://www.billcramer.com/new-gmc-sierra-3500hd"
                className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm font-mono text-xs" data-testid="inventory-url-input" />
            </div>
            <Button onClick={saveLink} disabled={savingLink || !linkForm.dealership_id || !linkForm.vehicle_make || !linkForm.vehicle_model || !linkForm.inventory_url}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11" data-testid="save-link-btn">
              {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingLink ? 'Update Link' : 'Add Link')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Make/Model Dialog */}
      <Dialog open={showAddMakeModel} onOpenChange={setShowAddMakeModel}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
              Add Make / Model
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Make</Label>
              <Input
                value={newMake}
                onChange={e => setNewMake(e.target.value)}
                placeholder="e.g. Ford, Cadillac, Chevrolet"
                className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm"
                data-testid="new-make-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Model</Label>
              <Input
                value={newModel}
                onChange={e => setNewModel(e.target.value)}
                placeholder="e.g. F-150, Escalade, Corvette"
                className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm"
                data-testid="new-model-input"
              />
            </div>
            <Button
              onClick={async () => {
                if (!newMake.trim() || !newModel.trim()) return;
                setAddingMakeModel(true);
                try {
                  const res = await axios.post(`${API}/vehicle-repository`, {
                    vehicle_make: newMake.trim(),
                    vehicle_model: newModel.trim(),
                    image_url: '',
                    build_price_url: ''
                  }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
                  setVehicles(prev => [...prev, res.data]);
                  toast.success(`Added ${newMake.trim()} ${newModel.trim()}`);
                  setNewMake('');
                  setNewModel('');
                  setShowAddMakeModel(false);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Failed to add');
                } finally {
                  setAddingMakeModel(false);
                }
              }}
              disabled={addingMakeModel || !newMake.trim() || !newModel.trim()}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11"
              data-testid="save-make-model-btn"
            >
              {addingMakeModel ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to Repository'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
